/** The basemap's own 3D buildings — one venue setting, three surfaces (item 0107).
 *
 *  Before this, the same decision was made three different ways: the viewer
 *  hid every fill-extrusion layer, the editor canvas hid them with a copy of
 *  the same loop, and the admin MiniMap never ran either loop — so the grey
 *  OSM boxes a user sees in the MiniMap were on by ACCIDENT, not by choice.
 *  `venue.settings.basemap_3d` now decides, and defaults to off, so every
 *  existing venue keeps the flat "Situm" city plan it has today.
 *
 *  `flatContext` is the second half of the same decision and must not be
 *  separated from it. Liberty draws flat footprints only at z13–14 and hands
 *  off to `building-3d` above that, so hiding the extrusions leaves an
 *  authoring map with no surroundings at all at venue zoom. The editor works
 *  around that by stretching the flat layer to z13–24. Turn the extrusions
 *  back ON without undoing that stretch and you get BOTH — flat footprints
 *  lying under solid boxes. Hence the snapshot: the flat layer's original zoom
 *  range and paint are captured before anything is touched, and restored when
 *  the extrusions come back, so the style's own hand-off resumes at every zoom
 *  rather than at the one that happened to get checked.
 */

const snaps = new WeakMap();

function snapshot(map) {
  const cached = snaps.get(map);
  if (cached) return cached;
  // Read BEFORE the first mutation, or "restore" restores our own edits.
  const layers = map.getStyle()?.layers ?? [];
  const snap = {
    extrusions: layers
      .filter((l) => l.type === "fill-extrusion")
      .map((l) => ({ id: l.id, visibility: l.layout?.visibility })),
    building: null,
  };
  const b = layers.find((l) => l.id === "building" && l.type === "fill");
  if (b) {
    snap.building = {
      minzoom: b.minzoom,
      maxzoom: b.maxzoom,
      visibility: b.layout?.visibility,
      color: b.paint?.["fill-color"],
      outline: b.paint?.["fill-outline-color"],
      opacity: b.paint?.["fill-opacity"],
    };
  }
  snaps.set(map, snap);
  return snap;
}

/** Apply the setting to a map whose style has loaded.
 *
 *  @param enabled       show the basemap's fill-extrusion layers
 *  @param flatContext   authoring surfaces (editor canvas, MiniMap) keep pale
 *                       flat footprints at every zoom while 3D is off, so the
 *                       author is not aiming at an empty background
 *  @returns how many fill-extrusion layers this basemap actually has — zero
 *           means the toggle has nothing to show and the UI should say so.
 */
export function applyBasemap3D(map, enabled, { flatContext = false } = {}) {
  if (!map || !map.getStyle) return 0;
  const snap = snapshot(map);

  for (const l of snap.extrusions) {
    if (!map.getLayer(l.id)) continue;
    // Restore the style's own visibility rather than forcing "visible": a
    // basemap may ship an extrusion layer that was hidden for its own reasons.
    map.setLayoutProperty(l.id, "visibility", enabled ? l.visibility ?? "visible" : "none");
  }

  const b = snap.building;
  if (b && map.getLayer("building")) {
    if (!enabled && flatContext) {
      map.setLayerZoomRange("building", 13, 24);
      map.setLayoutProperty("building", "visibility", "visible");
      map.setPaintProperty("building", "fill-color", "#c3c6cc");
      map.setPaintProperty("building", "fill-outline-color", "#9aa0ab");
      map.setPaintProperty("building", "fill-opacity", 0.7);
    } else if (flatContext) {
      // Hand the zoom range and the beige back to the basemap.
      map.setLayerZoomRange("building", b.minzoom ?? 0, b.maxzoom ?? 24);
      map.setLayoutProperty("building", "visibility", b.visibility ?? "visible");
      map.setPaintProperty("building", "fill-color", b.color ?? undefined);
      map.setPaintProperty("building", "fill-outline-color", b.outline ?? undefined);
      map.setPaintProperty("building", "fill-opacity", b.opacity ?? undefined);
    }
  }

  return snap.extrusions.length;
}

/** Read the setting. Absent means off, which is what every venue has today. */
export const basemap3dEnabled = (settings) => (settings ?? {}).basemap_3d === true;
